/**
 * Sichtbarkeits-Helper fuer den Marktplatz (Unternehmensgruppen-Feature).
 *
 * Listings koennen 'public' (alle Mandanten) oder 'group' (nur Mandanten
 * derselben Unternehmensgruppe / Konzern-Hierarchie im SSO) sein. Das
 * Backend liefert im Browse-Endpoint bereits nur die fuer den Mandanten
 * sichtbaren Listings — diese Helper steuern rein die UI-Darstellung
 * (Gruppen-Filter, Chips, Button-Beschriftung bei 0-€-Angeboten).
 */

export type ListingScope = 'all' | 'group';

export interface VisibilityAware {
    visibility?: string;
}

export const isGroupListing = (listing: VisibilityAware): boolean =>
    listing.visibility === 'group';

export const hasGroupListings = (listings: VisibilityAware[]): boolean =>
    listings.some(isGroupListing);

export const filterListingsByScope = <T extends VisibilityAware>(
    listings: T[],
    scope: ListingScope,
): T[] => (scope === 'group' ? listings.filter(isGroupListing) : listings);

/**
 * 0-€-Angebote (typisch: Konzern verteilt Inhalte kostenlos an die
 * Tochterhaeuser) sollen nicht wie ein Kauf wirken.
 */
export const purchaseButtonLabel = (
    price: string | null | undefined,
    purchasing: boolean,
): string => {
    if (purchasing) return 'Wird verarbeitet...';
    if (!price || parseFloat(price) === 0) return 'Kostenlos beziehen';
    return 'Jetzt lizenzieren';
};

/**
 * Anbieten und Kaufen liegen im Backend auf verschiedenen Rollen: eigene
 * Angebote darf der Content-Manager pflegen (`/api/marketplace/listings`),
 * Lizenzieren, Lizenzen und Verkaeufe verlangen ROLE_COMPANY_ADMIN.
 */
export const canLicenseCourses = (roles: string[]): boolean =>
    roles.some((role) =>
        ['ROLE_COMPANY_ADMIN', 'ROLE_OPERATOR', 'ROLE_PLATFORM_ADMIN'].includes(role),
    );

// ---------------------------------------------------------------------------
// Marktplatz-Rework T5 — Seller-Darstellung (Plan 3.4 „Unverwechselbare
// Darstellung"). Eine Stelle für Icon, Farbe und Textschlüssel beider
// Sichtbarkeiten, damit Tabelle, Formular und (T6) Karten identisch aussehen.
// Öffentlich = neutral/grau + Icon `storefront`; Unternehmensgruppe =
// Info-Akzent (`--oa-info`) + Icon `groups`. Beide tragen immer ein Textlabel —
// nie nur Farbe.
// ---------------------------------------------------------------------------

export type SellerVisibilityKey = 'public' | 'group';

export interface VisibilityMeta {
    /** Material-Symbols-Rounded-Icon (im Subset enthalten) */
    icon: string;
    /** CSS-Variable für Akzent (Rahmen, Icon, Text) */
    accentVar: string;
    /** Schlüssel unter `marketplaceSeller.visibility.*` */
    labelKey: string;
    shortLabelKey: string;
    hintKey: string;
}

export const VISIBILITY_META: Record<SellerVisibilityKey, VisibilityMeta> = {
    public: {
        icon: 'storefront',
        accentVar: 'var(--oa-text-2)',
        labelKey: 'marketplaceSeller.visibility.public.label',
        shortLabelKey: 'marketplaceSeller.visibility.public.short',
        hintKey: 'marketplaceSeller.visibility.public.hint',
    },
    group: {
        icon: 'groups',
        accentVar: 'var(--oa-info)',
        labelKey: 'marketplaceSeller.visibility.group.label',
        shortLabelKey: 'marketplaceSeller.visibility.group.short',
        hintKey: 'marketplaceSeller.visibility.group.hint',
    },
};

export const visibilityMeta = (visibility: string | null | undefined): VisibilityMeta =>
    VISIBILITY_META[visibility === 'group' ? 'group' : 'public'];

export type SellerListingStatusKey = 'draft' | 'in_review' | 'published' | 'rejected' | 'withdrawn';

export interface ListingStatusMeta {
    icon: string;
    /** neutral | info | success | warn | error — wird in der Komponente auf Tokens abgebildet */
    tone: 'neutral' | 'info' | 'success' | 'warn' | 'error';
    labelKey: string;
}

export const LISTING_STATUS_META: Record<SellerListingStatusKey, ListingStatusMeta> = {
    draft: { icon: 'edit', tone: 'neutral', labelKey: 'marketplaceSeller.status.draft' },
    in_review: { icon: 'hourglass_top', tone: 'warn', labelKey: 'marketplaceSeller.status.in_review' },
    published: { icon: 'check_circle', tone: 'success', labelKey: 'marketplaceSeller.status.published' },
    rejected: { icon: 'cancel', tone: 'error', labelKey: 'marketplaceSeller.status.rejected' },
    withdrawn: { icon: 'pause', tone: 'neutral', labelKey: 'marketplaceSeller.status.withdrawn' },
};

export const listingStatusMeta = (status: string | null | undefined): ListingStatusMeta =>
    LISTING_STATUS_META[(status as SellerListingStatusKey) in LISTING_STATUS_META ? (status as SellerListingStatusKey) : 'draft'];

/**
 * Gruppen-Optionen gibt es nur für Mandanten mit Unternehmensgruppe — sonst
 * werden sie gar nicht erst angezeigt (statt deaktiviert), um Verwechslungen
 * zu vermeiden (Plan 3.4).
 */
export const canOfferToGroup = (hasGroup: boolean): boolean => hasGroup;

/** Gruppen-Preislogik, die zur gewählten Sichtbarkeit passt (Plan 3.4 Tabelle). */
export const groupPricingOptionsFor = (visibility: SellerVisibilityKey): Array<'same' | 'free' | 'internal'> =>
    visibility === 'group' ? ['free', 'internal'] : ['free', 'same', 'internal'];

/** Kurzform „49,00 €" für Preisfelder aus der API (Strings mit Punkt-Dezimal). */
export const formatEuro = (amount: string | number | null | undefined, currency = 'EUR'): string => {
    const value = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (value === null || value === undefined || Number.isNaN(value)) return '–';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value);
};

// ---------------------------------------------------------------------------
// Marktplatz-Rework T6 — Buyer-Seite (Plan 3.4): Browse in zwei getrennten
// Bereichen, Herkunfts-Badge, Gruppenpreis, Abwicklung (PayPal / kostenlos /
// intern). Spiegelt `ListingVisibilityChecker::resolvePrice` — maßgeblich bleibt
// das Backend (Checkout), das Frontend nutzt es nur für die Darstellung.
// ---------------------------------------------------------------------------

export type BuyerLicenseType = 'reference' | 'copy' | 'seat_copy';
export type ListingSettlement = 'paypal' | 'free' | 'internal';

export interface BuyerListingLike {
    origin?: string;
    visibility?: string;
    groupPricing?: string;
    priceReference?: string | null;
    priceCopy?: string | null;
    priceSeat?: string | null;
    listPriceReference?: string | null;
    listPriceCopy?: string | null;
    listPriceSeat?: string | null;
    allowReference?: boolean;
    allowCopy?: boolean;
    allowSeatCopy?: boolean;
}

/**
 * Welches Herkunfts-Badge trägt ein Angebot für den Betrachter? Maßgeblich ist
 * die Herkunft (`origin`): Angebote von Mandanten der eigenen Unternehmensgruppe
 * sind „Gruppe“ (auch wenn sie öffentlich gelistet sind), alles andere
 * „Öffentlich“. Ohne `origin` (Bestandsantworten) entscheidet `visibility`.
 */
export const originKey = (listing: BuyerListingLike): SellerVisibilityKey => {
    if (listing.origin === 'group') return 'group';
    if (listing.origin === 'public') return 'public';
    return listing.visibility === 'group' ? 'group' : 'public';
};

/** Teilt die Angebote in die zwei Browse-Bereiche — jedes Angebot in genau einen. */
export const partitionByOrigin = <T extends BuyerListingLike>(
    listings: T[],
): Record<SellerVisibilityKey, T[]> => ({
    public: listings.filter((l) => originKey(l) === 'public'),
    group: listings.filter((l) => originKey(l) === 'group'),
});

/** Der Gruppen-Bereich erscheint nur, wenn der Mandant einer Gruppe angehört (Token). */
export const showGroupTab = (hasGroupInToken: boolean): boolean => hasGroupInToken;

export const allowedLicenseTypes = (listing: BuyerListingLike): BuyerLicenseType[] => {
    const types: BuyerLicenseType[] = [];
    if (listing.allowReference) types.push('reference');
    if (listing.allowCopy) types.push('copy');
    if (listing.allowSeatCopy) types.push('seat_copy');
    return types;
};

/** Beim Öffnen vorgewählte Lizenzart (#525: nie eine nicht erlaubte). */
export const firstAllowedLicenseType = (listing: BuyerListingLike): BuyerLicenseType =>
    allowedLicenseTypes(listing)[0] ?? 'reference';

const PRICE_FIELDS: Record<BuyerLicenseType, [keyof BuyerListingLike, keyof BuyerListingLike]> = {
    reference: ['priceReference', 'listPriceReference'],
    copy: ['priceCopy', 'listPriceCopy'],
    seat_copy: ['priceSeat', 'listPriceSeat'],
};

const toCents = (value: string | number | null | undefined): number => {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    return n === null || n === undefined || Number.isNaN(n) ? 0 : Math.round(n * 100);
};

/** Effektiver Einzelpreis für den Betrachter in Cent (Gruppenpreis bei Gruppenherkunft). */
export const unitPriceCents = (listing: BuyerListingLike, type: BuyerLicenseType): number =>
    toCents(listing[PRICE_FIELDS[type][0]] as string | null | undefined);

/** Listenpreis (für Fremde) in Cent; fehlt er, gilt der effektive Preis. */
export const listUnitPriceCents = (listing: BuyerListingLike, type: BuyerLicenseType): number => {
    const list = listing[PRICE_FIELDS[type][1]] as string | null | undefined;
    return list === undefined || list === null ? unitPriceCents(listing, type) : toCents(list);
};

export const totalPriceCents = (listing: BuyerListingLike, type: BuyerLicenseType, seats = 1): number =>
    unitPriceCents(listing, type) * (type === 'seat_copy' ? Math.max(1, seats) : 1);

export const totalListPriceCents = (listing: BuyerListingLike, type: BuyerLicenseType, seats = 1): number =>
    listUnitPriceCents(listing, type) * (type === 'seat_copy' ? Math.max(1, seats) : 1);

/** Abweichender Gruppenpreis → Listenpreis wird durchgestrichen angezeigt. */
export const hasGroupPriceDeviation = (listing: BuyerListingLike, type: BuyerLicenseType): boolean =>
    listing.origin === 'group'
    && listUnitPriceCents(listing, type) !== unitPriceCents(listing, type);

/** „Gruppenpreis: kostenlos“ / „Gruppenpreis: intern“ — nur bei Gruppenherkunft. */
export const groupPriceKind = (listing: BuyerListingLike, type: BuyerLicenseType): 'free' | 'internal' | null => {
    if (listing.origin !== 'group') return null;
    if (unitPriceCents(listing, type) === 0) return 'free';
    return resolveSettlement(listing, type, 1) === 'internal' ? 'internal' : null;
};

/**
 * Abwicklung des Kaufs (Plan 3.4 Tabelle, Spiegel von `ResolvedListingPrice`):
 * 0 € → kostenlos; Gruppenherkunft auf reinem Gruppen-Angebot oder mit internem
 * Verrechnungspreis → intern (Belastungsanzeige, nie PayPal); sonst PayPal.
 */
export const resolveSettlement = (
    listing: BuyerListingLike,
    type: BuyerLicenseType,
    seats = 1,
): ListingSettlement => {
    if (totalPriceCents(listing, type, seats) === 0) return 'free';
    if (listing.origin === 'group' && (listing.visibility === 'group' || listing.groupPricing === 'internal')) {
        return 'internal';
    }
    return 'paypal';
};

/** PayPal-SDK und Zahlungsschritt gibt es nur bei Abwicklung `paypal` (> 0 €, kein Gruppenpreis). */
export const requiresPayPal = (settlement: ListingSettlement): boolean => settlement === 'paypal';

export interface SeatUsage {
    used: number;
    max: number | null;
    /** 0–100, 0 wenn unbegrenzt */
    percent: number;
    full: boolean;
}

/** Platzbelegung einer Lizenz für den Seat-Balken. */
export const seatUsage = (license: { currentEnrollments?: number | null; maxEnrollments?: number | null }): SeatUsage => {
    const used = license.currentEnrollments ?? 0;
    const max = license.maxEnrollments ?? null;
    if (!max || max <= 0) return { used, max: null, percent: 0, full: false };
    return { used, max, percent: Math.min(100, Math.round((used / max) * 100)), full: used >= max };
};
